import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Anime, AnimeEpisode } from '../types/anime';
import {
  downloadEpisode,
  DownloadSite,
  EpisodeDownloadProgress,
  fetchRukaPlayers,
  formatBytes,
  getDownloadSite,
  isDownloadableEpisode,
  playerName,
  RukaPlayerOption,
  SITE_LABELS,
} from '../services/episodeDownloader';
import { getOfflineEpisodesForAnime, OfflineEpisode } from '../services/animeOfflineDB';
import { fetchWakuDetail, isDetailForAnime, mergeEpisodeLists } from '../services/animeService';
import { OfflineLibraryModal } from './OfflineLibraryModal';
import {
  X,
  Download,
  Loader2,
  CheckCircle2,
  AlertCircle,
  StopCircle,
  ExternalLink,
  Library,
  Check,
  Globe,
} from 'lucide-react';

interface AnimeDownloadModalProps {
  anime: Anime;
  episodes: AnimeEpisode[];
  /** Opened from one episode row: preselect just that episode */
  initialEpisode?: { number: number; audio: 'ซับไทย' | 'พากย์ไทย'; url?: string };
  onClose: () => void;
  /** A download finished (refresh the offline list) */
  onSavedToApp?: () => void;
  /** Watched to the end in the library's player */
  onWatched?: (episode: OfflineEpisode) => void;
}

type Audio = 'ซับไทย' | 'พากย์ไทย';
type OfflineState = 'complete' | 'partial';

// The player (tab on the episode page) to download from, remembered by its name
const PLAYER_KEY = 'download.preferredPlayer';
// The site to download from when both have the episode; AnimeRuka by default (sharpest picture)
const SITE_KEY = 'download.preferredSite';
const SITE_ORDER: DownloadSite[] = ['animeruka', 'anime-waku'];
const SPEED_WINDOW_MS = 5000;

function loadPreferredSite(): DownloadSite {
  try {
    const v = localStorage.getItem(SITE_KEY) as DownloadSite | null;
    return v && SITE_ORDER.includes(v) ? v : 'animeruka';
  } catch {
    return 'animeruka';
  }
}

function savePreferredSite(site: DownloadSite) {
  try {
    localStorage.setItem(SITE_KEY, site);
  } catch {
    // Storage blocked: the choice just isn't remembered
  }
}

function loadPreferredPlayer(): string {
  try {
    return localStorage.getItem(PLAYER_KEY) || '';
  } catch {
    return '';
  }
}

function savePreferredPlayer(label: string) {
  try {
    localStorage.setItem(PLAYER_KEY, label);
  } catch {
    // Storage blocked: the choice just isn't remembered
  }
}

/** The viewer's pick when this episode has it, else AnimeRuka's own player (sharpest), else the first that can be saved */
function choosePlayer(players: RukaPlayerOption[], preferred: string): RukaPlayerOption | undefined {
  const ok = players.filter((p) => p.downloadable);
  return (
    ok.find((p) => preferred && p.label.toLowerCase() === preferred.toLowerCase()) ||
    ok.find((p) => /ruka/i.test(p.label)) ||
    ok.find((p) => /waku/i.test(p.label)) ||
    ok[0]
  );
}

export const AnimeDownloadModal: React.FC<AnimeDownloadModalProps> = ({
  anime,
  episodes: shownEpisodes,
  initialEpisode,
  onClose,
  onSavedToApp,
  onWatched,
}) => {
  // The saved list may hold only another site's copies, so read Anime-Waku's and AnimeRuka's episode pages too
  const [extraEpisodes, setExtraEpisodes] = useState<AnimeEpisode[]>([]);
  const [isLoadingExtra, setIsLoadingExtra] = useState(false);
  useEffect(() => {
    const dlSources = anime.sources.filter(
      (s) => s.url.includes('animeruka.com/anime/') || s.url.includes('anime-waku.com/anime/')
    );
    if (dlSources.length === 0) return;
    setIsLoadingExtra(true);
    Promise.all(dlSources.map((s) => fetchWakuDetail(s.url).catch(() => null)))
      .then((details) =>
        setExtraEpisodes(
          mergeEpisodeLists(
            ...details
              .filter((d): d is NonNullable<typeof d> => !!d && isDetailForAnime(anime, d))
              .map((d) => d.episodes || [])
          )
        )
      )
      .finally(() => setIsLoadingExtra(false));
  }, [anime.id]);
  const episodes = useMemo(
    () => mergeEpisodeLists(shownEpisodes, extraEpisodes),
    [shownEpisodes, extraEpisodes]
  );

  // What is already in the app, per `${audio}_${number}`
  const [offline, setOffline] = useState<Map<string, OfflineState>>(new Map());
  const [offlineLoaded, setOfflineLoaded] = useState(false);
  const reloadOffline = () =>
    getOfflineEpisodesForAnime(anime.id)
      .then((list) =>
        setOffline(
          new Map(
            list.map((e) => [
              `${e.audio}_${e.number}`,
              e.savedSegments >= e.segmentCount ? 'complete' : 'partial',
            ])
          )
        )
      )
      .catch(() => {})
      .finally(() => setOfflineLoaded(true));
  useEffect(() => {
    reloadOffline();
  }, [anime.id]);

  const availableAudios = useMemo(
    () => (['ซับไทย', 'พากย์ไทย'] as Audio[]).filter((a) => episodes.some((e) => e.audio === a)),
    [episodes]
  );
  const [audio, setAudio] = useState<Audio>(initialEpisode?.audio || 'ซับไทย');
  useEffect(() => {
    if (availableAudios.length > 0 && !availableAudios.includes(audio)) setAudio(availableAudios[0]);
  }, [availableAudios.join()]);

  // Sites this audio can be downloaded from
  const availableSites = useMemo(() => {
    const sites = new Set<DownloadSite>();
    for (const ep of episodes) {
      if (ep.audio !== audio) continue;
      const site = getDownloadSite(ep.url);
      if (site) sites.add(site);
    }
    return SITE_ORDER.filter((site) => sites.has(site));
  }, [episodes, audio]);
  const [preferredSite, setPreferredSite] = useState<DownloadSite>(
    () => getDownloadSite(initialEpisode?.url) || loadPreferredSite()
  );
  const chosenSite = availableSites.includes(preferredSite) ? preferredSite : availableSites[0];
  const pickSite = (site: DownloadSite) => {
    setPreferredSite(site);
    savePreferredSite(site);
  };

  // One entry per episode number for the chosen audio; the chosen site's copy wins, then the other
  // downloadable site, then a watch-only link. The other site's copies are kept as automatic fallbacks.
  const { byNumber, fallbacksByNumber } = useMemo(() => {
    const map = new Map<number, AnimeEpisode>();
    const fallbacks = new Map<number, string[]>();
    const rankOf = (ep: AnimeEpisode) => {
      const site = getDownloadSite(ep.url);
      return !site ? 0 : site === chosenSite ? 2 : 1;
    };
    for (const ep of episodes) {
      if (ep.audio !== audio) continue;
      if (isDownloadableEpisode(ep)) {
        const list = fallbacks.get(ep.number) || [];
        if (!list.includes(ep.url)) list.push(ep.url);
        fallbacks.set(ep.number, list);
      }
      const prev = map.get(ep.number);
      if (!prev || rankOf(ep) > rankOf(prev)) map.set(ep.number, ep);
    }
    return {
      byNumber: [...map.values()].sort((a, b) => a.number - b.number),
      fallbacksByNumber: fallbacks,
    };
  }, [episodes, audio, chosenSite]);

  // Players offered by the chosen site's episode pages (read from the first one that can be downloaded)
  const sampleEp =
    byNumber.find((e) => getDownloadSite(e.url) === chosenSite) || byNumber.find(isDownloadableEpisode);
  const sampleSite = getDownloadSite(sampleEp?.url);
  const [players, setPlayers] = useState<RukaPlayerOption[]>([]);
  const [playersState, setPlayersState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [preferredPlayer, setPreferredPlayer] = useState(loadPreferredPlayer);
  // Lists already read, per episode page: switching audio / site back and forth doesn't fetch (and jump) again
  const playersCache = useRef(new Map<string, RukaPlayerOption[]>());
  useEffect(() => {
    if (!sampleEp) return;
    const url = sampleEp.url;
    const cached = playersCache.current.get(url);
    if (cached) {
      setPlayers(cached);
      setPlayersState('idle');
      return;
    }
    const controller = new AbortController();
    // The previous list stays up (dimmed) until the new one arrives, so the panel keeps its height
    setPlayersState('loading');
    fetchRukaPlayers(url, controller.signal)
      .then((list) => {
        playersCache.current.set(url, list);
        setPlayers(list);
        setPlayersState('idle');
      })
      .catch(() => {
        if (!controller.signal.aborted) setPlayersState('error');
      });
    return () => controller.abort();
  }, [sampleEp?.url]);
  const chosenPlayer = choosePlayer(players, preferredPlayer);
  const pickPlayer = (p: RukaPlayerOption) => {
    setPreferredPlayer(p.label);
    savePreferredPlayer(p.label);
  };

  const stateOf = (ep: AnimeEpisode) => offline.get(`${audio}_${ep.number}`);
  const canPick = (ep: AnimeEpisode) => isDownloadableEpisode(ep) && stateOf(ep) !== 'complete';

  // Picked episode numbers (for the current audio)
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const pickedInitial = useRef(false);
  useEffect(() => {
    if (byNumber.length === 0 || pickedInitial.current || !offlineLoaded) return;
    if (isLoadingExtra && !byNumber.some(isDownloadableEpisode)) return;
    pickedInitial.current = true;
    if (initialEpisode && audio === initialEpisode.audio) {
      const ep = byNumber.find((e) => e.number === initialEpisode.number);
      setPicked(new Set(ep && canPick(ep) ? [ep.number] : []));
      return;
    }
    // Default: the next unwatched episode only
    const next = byNumber.find((e) => e.number > anime.watchedEp && canPick(e));
    setPicked(new Set(next ? [next.number] : []));
  }, [byNumber, isLoadingExtra, offlineLoaded]);

  const switchAudio = (a: Audio) => {
    setAudio(a);
    setPicked(new Set());
  };
  const toggle = (n: number) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  const pickWhere = (pred: (e: AnimeEpisode) => boolean) =>
    setPicked(new Set(byNumber.filter((e) => canPick(e) && pred(e)).map((e) => e.number)));

  const toDownload = byNumber.filter((e) => picked.has(e.number) && canPick(e));

  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState<EpisodeDownloadProgress | null>(null);
  // Download speed over the last few seconds (bytes/s), for the speed and time-left line
  const [speed, setSpeed] = useState(0);
  const speedSamples = useRef<{ t: number; bytes: number }[]>([]);
  const onProgress = (p: EpisodeDownloadProgress) => {
    setProgress(p);
    const samples = speedSamples.current;
    // A new episode (or the resolving step) starts the measurement over
    if (p.status !== 'downloading' || (samples.length > 0 && p.bytes < samples[samples.length - 1].bytes)) {
      samples.length = 0;
      setSpeed(0);
      if (p.status !== 'downloading') return;
    }
    const now = performance.now();
    samples.push({ t: now, bytes: p.bytes });
    while (samples.length > 2 && now - samples[0].t > SPEED_WINDOW_MS) samples.shift();
    const span = now - samples[0].t;
    if (span >= 1000) setSpeed(((p.bytes - samples[0].bytes) / span) * 1000);
  };
  const [completed, setCompleted] = useState<{ ep: number; title: string; bytes: number; source: string }[]>([]);
  const [failed, setFailed] = useState<{ ep: number; reason: string }[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const [showLibrary, setShowLibrary] = useState(false);

  // Leaving mid-download cancels it
  useEffect(() => () => abortRef.current?.abort(), []);

  const handleStart = async () => {
    if (toDownload.length === 0) return;
    setIsDownloading(true);
    setCompleted([]);
    setFailed([]);
    setProgress(null);
    const controller = new AbortController();
    abortRef.current = controller;
    // Keep the screen on: a phone that dims and locks pauses the page, and the download with it
    let wakeLock: { release: () => Promise<void> } | null = null;
    try {
      wakeLock = (await (navigator as any).wakeLock?.request('screen')) ?? null;
    } catch {
      // Not supported / not allowed: the download still runs while the screen stays on
    }

    for (const ep of toDownload) {
      if (controller.signal.aborted) break;
      try {
        const fallbacks = (fallbacksByNumber.get(ep.number) || []).filter((u) => u !== ep.url);
        const result = await downloadEpisode(
          ep,
          anime,
          controller.signal,
          onProgress,
          chosenPlayer?.label || '',
          fallbacks
        );
        setCompleted((prev) => [...prev, { ep: ep.number, ...result }]);
        setPicked((prev) => {
          const next = new Set(prev);
          next.delete(ep.number);
          return next;
        });
      } catch (err: any) {
        if (err?.name === 'AbortError') break;
        setFailed((prev) => [...prev, { ep: ep.number, reason: err?.message || 'ไม่ทราบสาเหตุ' }]);
      }
      await reloadOffline();
    }

    abortRef.current = null;
    wakeLock?.release().catch(() => {});
    setIsDownloading(false);
    setProgress(null);
    setSpeed(0);
    await reloadOffline();
    onSavedToApp?.();
  };

  const handleCancel = () => abortRef.current?.abort();

  const pct =
    progress && progress.totalSegments > 0
      ? Math.round((progress.doneSegments / progress.totalSegments) * 100)
      : 3;
  // Time left for this episode: the segments still to come at the average segment size so far
  const secondsLeft =
    progress && speed > 0 && progress.doneSegments > 0
      ? ((progress.totalSegments - progress.doneSegments) * (progress.bytes / progress.doneSegments)) / speed
      : 0;
  const hasWebOnly = byNumber.some((e) => !isDownloadableEpisode(e));
  const showDoneFooter = !isDownloading && completed.length > 0 && toDownload.length === 0;

  return (
    <div
      onClick={isDownloading ? undefined : onClose}
      className="fixed inset-0 bg-black/80 z-[60] flex items-end sm:items-center justify-center sm:p-4 safe-area-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-900 border border-teal-500/40 rounded-t-3xl sm:rounded-2xl max-w-md w-full max-h-[92vh] supports-[height:100dvh]:max-h-[92dvh] flex flex-col shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="px-4 sm:px-5 py-3 border-b border-slate-800 flex items-center gap-2 shrink-0">
          <Download className="w-5 h-5 text-teal-400 shrink-0" />
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-bold text-white leading-tight">ดาวน์โหลดตอน</h3>
            <p className="text-[11px] text-slate-400 truncate">{anime.title}</p>
          </div>
          <button
            type="button"
            onClick={() => setShowLibrary(true)}
            className="h-9 px-3 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 text-xs font-bold flex items-center gap-1.5 shrink-0"
          >
            <Library className="w-4 h-4" />
            <span>คลัง</span>
          </button>
          {!isDownloading && (
            <button
              type="button"
              onClick={onClose}
              title="ปิด"
              className="h-9 w-9 grid place-items-center text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5 space-y-4 flex-1 overflow-y-auto overscroll-contain">
          {/* Audio */}
          <div className="relative grid grid-cols-2 gap-1 p-1 rounded-2xl bg-slate-950 border border-slate-800">
            {/* Highlight slides between the two instead of jumping */}
            <div
              className="absolute top-1 bottom-1 left-1 w-[calc(50%-0.375rem)] rounded-xl bg-teal-600 shadow transition-transform duration-200 ease-out"
              style={{ transform: audio === 'พากย์ไทย' ? 'translateX(calc(100% + 0.25rem))' : undefined }}
            />
            {(['ซับไทย', 'พากย์ไทย'] as Audio[]).map((a) => {
              const has = availableAudios.includes(a);
              return (
                <button
                  key={a}
                  type="button"
                  disabled={!has || isDownloading}
                  onClick={() => switchAudio(a)}
                  className={`relative h-10 rounded-xl text-sm font-bold transition-colors duration-200 disabled:opacity-35 ${
                    audio === a ? 'text-white' : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {a}
                  {!has && <span className="text-[10px] font-medium"> (ไม่มี)</span>}
                </button>
              );
            })}
          </div>

          {/* Which player to download from */}
          {sampleEp && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-slate-200">โหลดจากที่ไหน</span>
                {playersState === 'loading' && (
                  <span className="text-[11px] text-slate-500 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> กำลังเช็คตัวเล่น...
                  </span>
                )}
              </div>
              {/* Which website (when both have this audio), then which of its players */}
              <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-slate-950/80 border border-slate-800 text-xs">
                <span className="flex items-center gap-2 text-slate-300">
                  <Globe className="w-4 h-4 text-teal-400 shrink-0" />
                  เว็บ
                </span>
                {availableSites.length > 1 ? (
                  <div className="flex items-center gap-1">
                    {availableSites.map((site) => (
                      <button
                        key={site}
                        type="button"
                        disabled={isDownloading}
                        onClick={() => pickSite(site)}
                        className={`px-2.5 py-1 rounded-lg font-bold transition-colors duration-200 disabled:opacity-40 ${
                          chosenSite === site
                            ? 'bg-teal-600 text-white shadow'
                            : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                        }`}
                      >
                        {SITE_LABELS[site]}
                      </button>
                    ))}
                  </div>
                ) : (
                  sampleSite && <span className="font-bold text-white">{SITE_LABELS[sampleSite]}</span>
                )}
              </div>
              {playersState === 'error' ? (
                <p className="text-[11px] text-slate-500">
                  เช็ครายชื่อตัวเล่นไม่ได้ — จะลองตัวเล่นหลักก่อน ถ้าไม่มีจะใช้ตัวอื่นที่โหลดได้
                </p>
              ) : (
                players.length > 0 && (
                  <div
                    className={`flex flex-wrap gap-1.5 transition-opacity duration-200 ${
                      playersState === 'loading' ? 'opacity-40 pointer-events-none' : ''
                    }`}
                  >
                    {players.map((p) => {
                      const active = chosenPlayer?.nume === p.nume;
                      return (
                        <button
                          key={p.nume}
                          type="button"
                          disabled={!p.downloadable || isDownloading}
                          onClick={() => pickPlayer(p)}
                          title={p.host || undefined}
                          className={`px-3 py-1.5 rounded-xl border text-xs font-bold text-left leading-tight transition-colors duration-200 ${
                            active
                              ? 'bg-teal-600 border-teal-400 text-white'
                              : p.downloadable
                                ? 'bg-slate-800/80 border-slate-700 text-slate-200 hover:border-slate-500'
                                : 'border-dashed border-slate-700 text-slate-500'
                          } disabled:cursor-not-allowed`}
                        >
                          ตัวเล่น {p.nume} · {p.label}
                          <span className={`block text-[10px] font-medium ${active ? 'text-teal-100' : 'text-slate-500'}`}>
                            {p.downloadable ? (p.host ? `ไฟล์จาก ${p.host}` : 'โหลดได้') : 'โหลดไม่ได้'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )
              )}
              {chosenPlayer && (
                <p className="text-[11px] text-slate-500">
                  จะโหลดจาก{' '}
                  <span className="text-teal-300 font-semibold">{playerName(chosenPlayer, sampleSite)}</span>
                  {' · '}ตอนไหนไม่มีตัวเล่นหรือเว็บนี้ จะใช้ตัวอื่นแทนและบอกไว้ในผลลัพธ์
                </p>
              )}
            </div>
          )}

          {/* Episode picker */}
          <div className="space-y-2.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-bold text-slate-200">
                เลือกตอน
                {picked.size > 0 && <span className="text-teal-300"> · {toDownload.length} ตอน</span>}
              </span>
              {isLoadingExtra && (
                <span className="text-[11px] text-slate-500 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> กำลังเช็คตอน...
                </span>
              )}
            </div>

            {!isDownloading && byNumber.length > 0 && (
              <div className="grid grid-cols-3 gap-1.5">
                <button
                  type="button"
                  onClick={() => pickWhere((e) => e.number > anime.watchedEp)}
                  className="h-9 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold"
                >
                  ที่ยังไม่ดู
                </button>
                <button
                  type="button"
                  onClick={() => pickWhere(() => true)}
                  className="h-9 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold"
                >
                  ทั้งหมด
                </button>
                <button
                  type="button"
                  onClick={() => setPicked(new Set())}
                  className="h-9 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 text-xs font-semibold"
                >
                  ล้าง
                </button>
              </div>
            )}

            {byNumber.length === 0 ? (
              <div className="py-8 text-center text-xs text-slate-500 bg-slate-950/60 rounded-xl border border-slate-800">
                {isLoadingExtra ? 'กำลังโหลดรายการตอน...' : 'ยังไม่มีรายการตอนของเสียงนี้'}
              </div>
            ) : (
              <div className="grid grid-cols-5 gap-2">
                {byNumber.map((ep) => {
                  const state = stateOf(ep);
                  const downloadable = isDownloadableEpisode(ep);
                  const isPicked = picked.has(ep.number) && canPick(ep);
                  const isActive = isDownloading && progress?.episodeNum === ep.number;
                  const base =
                    'relative h-12 rounded-xl text-base font-extrabold flex flex-col items-center justify-center leading-none transition-all active:scale-95';

                  if (!downloadable) {
                    return (
                      <a
                        key={ep.number}
                        href={ep.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="ตอนนี้ไม่มีบน Anime-Waku / AnimeRuka — เปิดดูบนเว็บ"
                        className={`${base} border border-dashed border-slate-700 text-slate-500`}
                      >
                        {ep.number}
                        <ExternalLink className="w-2.5 h-2.5 mt-1" />
                      </a>
                    );
                  }
                  if (state === 'complete') {
                    return (
                      <div
                        key={ep.number}
                        title="โหลดไว้ในแอปแล้ว"
                        className={`${base} bg-emerald-500/10 border border-emerald-500/40 text-emerald-300`}
                      >
                        {ep.number}
                        <Check className="w-3 h-3 mt-0.5" strokeWidth={3} />
                      </div>
                    );
                  }
                  return (
                    <button
                      key={ep.number}
                      type="button"
                      disabled={isDownloading}
                      onClick={() => toggle(ep.number)}
                      className={`${base} border ${
                        isPicked
                          ? 'bg-teal-600 border-teal-400 text-white shadow-lg shadow-teal-900/40'
                          : 'bg-slate-800/80 border-slate-700 text-slate-200 hover:border-slate-500'
                      } ${isActive ? 'animate-pulse' : ''} disabled:active:scale-100`}
                    >
                      {ep.number}
                      {state === 'partial' && (
                        <span className={`text-[9px] font-bold mt-0.5 ${isPicked ? 'text-teal-100' : 'text-amber-300'}`}>
                          ค้าง
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {byNumber.length > 0 && (
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-500">
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded bg-teal-600" /> เลือกแล้ว
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded border border-emerald-500/60 bg-emerald-500/10" /> อยู่ในคลังแล้ว
                </span>
                {hasWebOnly && (
                  <span className="flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded border border-dashed border-slate-600" /> ไม่มีบน Anime-Waku /
                    AnimeRuka (กดเพื่อเปิดเว็บ)
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Progress */}
          {isDownloading && (
            <div className="bg-slate-950/80 border border-teal-500/40 rounded-2xl p-3.5 space-y-2.5">
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="font-semibold text-teal-300 flex items-center gap-1.5 min-w-0">
                  <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                  <span className="truncate">{progress?.message || 'กำลังเริ่ม...'}</span>
                </span>
                {progress && progress.totalSegments > 0 && (
                  <span className="text-slate-400 font-mono text-[11px] shrink-0">
                    {pct}% · {formatBytes(progress.bytes)}
                  </span>
                )}
              </div>
              <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-teal-500 to-emerald-500 h-full transition-all duration-150"
                  style={{ width: `${pct}%` }}
                />
              </div>
              {speed > 0 && (
                <p className="text-[11px] text-slate-300 font-mono">
                  ความเร็ว {(speed / 1024 ** 2).toFixed(1)} MB/s
                  {secondsLeft > 0 && (
                    <> · เหลือ{secondsLeft < 60 ? 'ไม่ถึง 1 นาที' : `ประมาณ ${Math.ceil(secondsLeft / 60)} นาที`}</>
                  )}
                </p>
              )}
              <p className="text-[11px] text-slate-500">
                ตอนที่ {completed.length + failed.length + 1} จาก {toDownload.length + completed.length} —
                อย่าปิดหน้านี้หรือสลับไปแอปอื่นระหว่างโหลด
              </p>
            </div>
          )}

          {/* Results */}
          {!isDownloading && (completed.length > 0 || failed.length > 0) && (
            <div className="bg-slate-950/80 border border-slate-800 rounded-2xl p-3 space-y-2">
              {completed.length > 0 && (
                <div className="flex items-start gap-1.5 text-xs text-emerald-400">
                  <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    โหลดเสร็จ {completed.length} ตอน ·{' '}
                    {formatBytes(completed.reduce((s, c) => s + c.bytes, 0))} — ดูได้ในคลัง
                    <ul className="mt-0.5 space-y-0.5 text-[11px] text-slate-400">
                      {completed.map((c) => (
                        <li key={c.ep}>
                          ตอนที่ {c.ep}
                          {c.source && <> · จาก {c.source}</>}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              {failed.length > 0 && (
                <div className="flex items-start gap-1.5 text-xs text-rose-400">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <span>โหลดไม่ได้ {failed.length} ตอน:</span>
                    <ul className="list-disc pl-4 mt-0.5 space-y-0.5 text-[11px] text-slate-400">
                      {failed.map((f) => (
                        <li key={f.ep}>
                          ตอนที่ {f.ep}: {f.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          {!isDownloading && (
            <p className="text-[11px] text-slate-500">
              เก็บไว้ในแอปบนเครื่องนี้ ตอนละประมาณ 600–900 MB · ถ้าโหลดค้าง กดโหลดตอนเดิมอีกครั้งจะโหลดต่อจากเดิม
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-5 py-3 border-t border-slate-800 shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {isDownloading ? (
            <button
              type="button"
              onClick={handleCancel}
              className="w-full h-12 bg-rose-600/15 hover:bg-rose-600/25 text-rose-300 border border-rose-500/40 rounded-2xl text-sm font-bold flex items-center justify-center gap-2"
            >
              <StopCircle className="w-5 h-5" />
              <span>หยุดดาวน์โหลด</span>
            </button>
          ) : showDoneFooter ? (
            <button
              type="button"
              onClick={() => setShowLibrary(true)}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl text-sm font-bold shadow flex items-center justify-center gap-2 active:scale-[0.98]"
            >
              <Library className="w-5 h-5" />
              <span>ไปที่คลังดาวน์โหลด</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleStart}
              disabled={toDownload.length === 0}
              className="w-full h-12 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-800 disabled:text-slate-500 text-white rounded-2xl text-sm font-bold shadow flex items-center justify-center gap-2 active:scale-[0.98] disabled:active:scale-100"
            >
              <Download className="w-5 h-5" />
              <span>
                {toDownload.length === 0
                  ? 'แตะเลขตอนที่ต้องการโหลด'
                  : toDownload.length <= 5
                    ? `ดาวน์โหลด ${toDownload.length} ตอน (${toDownload.map((e) => e.number).join(', ')})`
                    : `ดาวน์โหลด ${toDownload.length} ตอน`}
              </span>
            </button>
          )}
        </div>

        <OfflineLibraryModal isOpen={showLibrary} onClose={() => setShowLibrary(false)} onWatched={onWatched} />
      </div>
    </div>
  );
};
